import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Inbox, Star, Eye, Home, Key, CheckCircle2, TrendingUp, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import api from "@/lib/api";
import { PageHeader, EmailStatusBanner } from "@/components/admin/PageHeader";
import { formatMoney, formatDateTime } from "@/lib/format";

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 text-[#C81D35] animate-spin" /></div>;
  const s = data.stats;

  const cards = [
    { label: "Total properties", value: s.total_properties, icon: Building2, sub: `${s.active_properties} active` },
    { label: "Total enquiries", value: s.total_enquiries, icon: Inbox, sub: `${s.new_enquiries} new`, accent: true },
    { label: "For sale", value: s.for_sale, icon: Home },
    { label: "To let", value: s.to_let, icon: Key },
    { label: "Featured", value: s.featured_properties, icon: Star },
    { label: "Unread enquiries", value: s.unread_enquiries, icon: Eye },
    { label: "Referred leads", value: s.referred, icon: TrendingUp },
    { label: "Converted", value: s.converted, icon: CheckCircle2 },
  ];

  const sourceData = Object.entries(data.enquiries_by_source || {}).map(([name, value]) => ({ name, value }));
  const COLORS = ["#C81D35", "#C5A059", "#111827", "#721C24", "#9CA3AF"];

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Overview of your platform activity" />
      <EmailStatusBanner configured={s.email_configured} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className={`rounded-xl border p-5 ${c.accent ? "bg-[#C81D35] text-white border-[#C81D35]" : "bg-white border-border"}`} data-testid={`stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center justify-between">
              <c.icon className={`w-5 h-5 ${c.accent ? "text-white/80" : "text-[#C81D35]"}`} />
            </div>
            <p className={`mt-3 text-3xl font-bold font-display ${c.accent ? "text-white" : "text-[#111827]"}`}>{c.value ?? 0}</p>
            <p className={`text-xs ${c.accent ? "text-white/80" : "text-muted-foreground"}`}>{c.label}{c.sub ? ` · ${c.sub}` : ""}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-border p-6">
          <h3 className="font-semibold text-[#111827] mb-4">Recent enquiries</h3>
          {data.recent_enquiries.length === 0 ? <p className="text-sm text-muted-foreground">No enquiries yet.</p> : (
            <div className="divide-y divide-border">
              {data.recent_enquiries.map((e) => (
                <Link key={e.id} to={`/admin/enquiries/${e.id}`} className="flex items-center justify-between py-3 hover:bg-muted/40 -mx-2 px-2 rounded-lg" data-testid={`recent-enq-${e.id}`}>
                  <div>
                    <p className="font-medium text-[#111827] text-sm">{e.full_name} {!e.read && <span className="ml-2 text-[10px] bg-[#C81D35] text-white px-1.5 py-0.5 rounded-full">NEW</span>}</p>
                    <p className="text-xs text-muted-foreground">{e.property_title || "General"} · {formatDateTime(e.created_at)}</p>
                  </div>
                  <span className="text-xs font-semibold px-2 py-1 rounded-full bg-muted">{e.status}</span>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-border p-6">
          <h3 className="font-semibold text-[#111827] mb-4">Enquiries by source</h3>
          {sourceData.length === 0 ? <p className="text-sm text-muted-foreground">No data yet.</p> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={sourceData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>{sourceData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-border p-6">
          <h3 className="font-semibold text-[#111827] mb-4">Most viewed properties</h3>
          {data.most_viewed.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2 text-sm">
              <Link to={`/property/${p.slug}`} className="text-[#111827] hover:text-[#C81D35] truncate">{p.title}</Link>
              <span className="text-muted-foreground flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {p.views || 0}</span>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-border p-6">
          <h3 className="font-semibold text-[#111827] mb-4">Recent activity</h3>
          {data.recent_activity.map((a) => (
            <div key={a.id} className="py-2 text-sm border-b border-border/60 last:border-0">
              <p className="text-[#111827]">{a.summary || a.action}</p>
              <p className="text-xs text-muted-foreground">{a.user_name} · {formatDateTime(a.created_at)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
